import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CheckCircle2, CircleDot } from 'lucide-react'
import { BackButton } from '@/components/back-button'
import { AutomationSwitch } from '@/components/automation-switch'
import { ManualStatusControl } from '@/components/manual-status-control'
import { ProjectPriorityPicker } from '@/components/priority-picker'
import { ProjectServiceTierPicker } from '@/components/service-tier'
import { asServiceTier } from '@/lib/service-tier'
import { ProjectNeedByPicker } from '@/components/need-by-picker'
import { PROJECT_STATUS_CHOICES } from '@/components/manual-status-options'
import {
  useProject,
  useProjectApCostSummary,
  useProjectTimeline,
  useProjectAutomationHistory,
  useUpdateProject,
  useProjectActions,
  useProjectDeliveryTimeline,
  useCustomerCommsByProject,
  useCorrectProjectStatus,
} from '@/api/hooks'
import { useProjectCommunications } from '@/features/communications/hooks'
import { queryKeys } from '@/api/queries'
import { formatMoney, formatStatus } from '@/lib/utils'
import { useActionFocus } from '@/hooks/use-action-focus'
import { purchasedShippingService } from '@/lib/purchased-shipping'
import { TimelineList } from '@/components/timeline'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CommentForm,
  CommentList,
} from '@/components/comments'
import { ProjectActivityBadges } from '@/components/entity-activity-badge'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import {
  ProjectFinancialMetrics,
  ProjectManualGroup,
  ProjectPOGroup,
  ProjectUnassignedGroup,
} from '@/components/project-po-groups'
import { ProjectDeliveryDates, StartedDate } from '@/components/delivery-timeline'
import {
  calculateProjectFinancials,
  groupProjectOrderLines,
} from '@/components/project-po-utils'
import { StatusPill } from '@/components/status-pill'
import { OrderStatusChips } from '@/components/order-status-chips'
import { BulkPOStatusPicker } from '@/components/bulk-po-status-picker'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { projectDisplayStatus } from '@/lib/status'
import { fulfillmentTone, paymentTone, sentenceCase } from '@/lib/status-tones'
import { ProjectDocumentsCard } from '@/features/projects/project-documents'
import { CancelProjectButton } from '@/features/projects/cancel-project-button'
import { ProjectActionList } from '@/features/projects/project-action-list'
import { ProjectCustomerCard } from '@/features/projects/project-customer-card'
import { ProjectExpectedTimeline } from '@/features/projects/project-expected-timeline'
import { ProjectMoneyCard } from '@/features/projects/project-money-card'
import { FOLD_COUNT, FoldToggle } from '@/features/projects/fold-toggle'
import {
  billsThroughInvoices,
  computeProjectProgress,
  progressSummary,
  stepTooltip,
  type ProgressOrder,
} from '@/features/projects/project-progress'
import { useProjectPayments } from '@/features/quote-builder/invoicing/project-payments'
import { HeldPurchaseOrderDetails } from '@/features/quote-builder/project/po-review'
import { usePoReviewMarks } from '@/features/quote-builder/project/po-review-marks'
import type { components } from '@/api/schema'
import { usePageTitle } from '@/lib/page-title'

type UserRead = components['schemas']['UserRead']

// ---------------------------------------------------------------------------
// Documents panels (unchanged)
// ---------------------------------------------------------------------------

interface ProjectDetailPageProps {
  projectId: string
  actionUuid?: string
}

// ---------------------------------------------------------------------------
// Project settings panel
// ---------------------------------------------------------------------------

function ProjectSettingsPanel({
  projectId,
  projectName,
  projectStatus,
  trackingEnabled,
}: {
  projectId: string
  projectName: string
  projectStatus: string
  trackingEnabled: boolean
}) {
  const updateProject = useUpdateProject(projectId)
  const history = useProjectAutomationHistory(projectId)
  const queryClient = useQueryClient()

  const handleToggle = (checked: boolean) => {
    const detailKey = queryKeys.projects.detail(projectId)
    queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) =>
      old ? { ...old, tracking_enabled: checked } : old,
    )
    updateProject.mutate({ tracking_enabled: checked })
  }

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Settings
        </h3>
        <AutomationSwitch
          id="tracking-toggle"
          description="PO emails, shipping, fulfillment, and customer notifications"
          checked={trackingEnabled}
          onCheckedChange={handleToggle}
          disabled={updateProject.isPending}
          history={history.data}
          historyLoading={history.isLoading}
        />
        {projectStatus !== 'cancelled' && (
          <div className="border-t pt-3">
            <CancelProjectButton projectId={projectId} projectName={projectName} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Timeline panel
// ---------------------------------------------------------------------------

function TimelinePanel({ projectId }: { projectId: string }) {
  const { data: events, isLoading } = useProjectTimeline(projectId, { limit: 200 })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!events?.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
        <CircleDot className="h-6 w-6" />
        <p className="text-sm">No events yet</p>
      </div>
    )
  }

  // A bounded, scrolling list: the timeline is history, not the page.
  return (
    <div className="max-h-[32rem] overflow-y-auto overscroll-contain rounded-lg border bg-card px-3 py-2">
      <TimelineList events={events} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Progress stepper
// ---------------------------------------------------------------------------

function ProjectProgressStepper({ orders, poReview, billedThroughInvoices }: {
  orders: ProgressOrder[]
  poReview: boolean
  billedThroughInvoices: boolean
}) {
  const { steps, currentIndex } = computeProjectProgress(orders, { billedThroughInvoices })

  return (
    <div className="flex flex-col items-start gap-1 md:items-end">
      <div className="flex items-center">
        {steps.map((step, i) => {
          const isComplete = step.status === 'complete'
          const isPartial = step.status === 'partial'
          const isCurrent = i === currentIndex

          const tooltipText = stepTooltip(step)

          return (
            <div key={step.key} className="flex items-center">
              {i > 0 && (
                <div
                  className={`h-[2px] w-5 ${
                    isComplete || isPartial || isCurrent ? 'bg-emerald-400' : 'bg-gray-200'
                  }`}
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    data-step={step.key}
                    data-step-status={step.status}
                    className={`h-3.5 w-3.5 rounded-full shrink-0 transition-colors ${
                      isComplete
                        ? 'bg-emerald-500'
                        : isPartial
                          ? 'bg-amber-400 ring-2 ring-amber-200'
                          : isCurrent
                            ? 'bg-gray-300 ring-2 ring-gray-200'
                            : 'bg-gray-200'
                    }`}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6}>
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            </div>
          )
        })}
      </div>
      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
        {progressSummary(steps, currentIndex, poReview)}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PM assignment
// ---------------------------------------------------------------------------

function ProjectStaffAssignment({
  projectId,
  label,
  idField,
  objField,
  currentUser,
  attention,
  className,
}: {
  projectId: string
  label: string
  idField: string
  objField: string
  currentUser?: UserRead | null
  /** The role owns an open action item on this project. */
  attention?: boolean
  className?: string
}) {
  const updateProject = useUpdateProject(projectId)
  const queryClient = useQueryClient()

  const handleSelect = (user: UserRead | null) => {
    const detailKey = queryKeys.projects.detail(projectId)
    queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) =>
      old ? { ...old, [idField]: user?.id ?? null, [objField]: user ?? null } : old,
    )
    updateProject.mutate({ [idField]: user?.id ?? null })
  }

  return (
    <StaffAssignmentPicker
      label={label}
      currentUser={currentUser}
      pending={updateProject.isPending}
      attention={attention}
      onChange={handleSelect}
      className={className}
    />
  )
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function ProjectDetailPage({ projectId, actionUuid }: ProjectDetailPageProps) {
  const { data: project, isLoading } = useProject(projectId)
  usePageTitle(project?.name)
  const correctStatus = useCorrectProjectStatus(projectId)
  const { data: apCosts, isLoading: apCostsLoading, isError: apCostsError } = useProjectApCostSummary(projectId)
  // Supplier threads only feed the header's in-flight badges; their emails live on each PO.
  const { data: threads } = useProjectCommunications(projectId)
  const { data: customerThreads, isLoading: customerThreadsLoading } = useCustomerCommsByProject(
    project?.id ?? 0,
  )
  const { data: projectActions = [], isSuccess: projectActionsLoaded } = useProjectActions(projectId)
  const deliveryTimeline = useProjectDeliveryTimeline(projectId)
  // The same query the Payments card reads: whether this project bills through our invoices.
  const { data: customerPayments } = useProjectPayments(projectId)
  useActionFocus(actionUuid, projectActionsLoaded && !!project)
  // Every role an open item is assigned or shared to; their team pills are highlighted.
  const actionRoles = new Set(
    projectActions
      .flatMap((action) => [action.assigned_role, ...(action.shared_roles ?? [])])
      .map((role) => (role === 'operations' ? 'ops' : role)),
  )
  const [selectedPOIds, setSelectedPOIds] = useState<Set<number>>(() => new Set())
  // Orders whose purchase orders are all shown, past the first three.
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(() => new Set())
  // A project started from a quote holds its POs until Sales has reviewed each one (USA-1333).
  const poReview = usePoReviewMarks(projectId)

  // The delivery dates in the header come from their own request; wait for them so the header
  // doesn't grow and push the page down a moment after it appears (USA-1536).
  if (isLoading || deliveryTimeline.isLoading) {
    return (
      <div className="mx-auto max-w-[1600px]">
        {/* Header skeleton: the real header's rows at their real heights, so nothing jumps when it
            loads (USA-1536): back link, title, status chips, delivery dates, team pills. */}
        <div className="mb-5">
          <Skeleton className="h-8 w-36" />
          <div className="mt-1 space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <Skeleton className="h-8 w-64 max-w-full" />
                <Skeleton className="mt-1.5 h-6 w-80 max-w-full" />
                <Skeleton className="mt-2 h-12 w-96 max-w-full max-sm:h-[4.5rem]" />
              </div>
              <div className="hidden gap-5 md:flex">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-1">
                    <Skeleton className="ml-auto h-3 w-12" />
                    <Skeleton className="ml-auto h-5 w-16" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="grid grid-cols-2 gap-1.5 sm:flex">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 rounded-full sm:w-28" />
                ))}
              </div>
              <Skeleton className="h-8 w-full md:w-72" />
            </div>
          </div>
        </div>
        {/* Content skeleton */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
          <div className="space-y-5">
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="mx-auto max-w-[1600px]">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    )
  }

  const orders = project.orders ?? []

  const allPurchaseOrders = orders.flatMap((o) => o.purchase_orders ?? [])
  const documents = project.documents ?? []
  const selectedPOs = allPurchaseOrders.filter((po) => selectedPOIds.has(po.id))
  const timelinesByPO = new Map(
    (deliveryTimeline.data ?? []).map((timeline) => [timeline.purchase_order_id, timeline]),
  )
  const selectedReviewable = selectedPOs.filter((po) => po.po_review === 'needs_review')

  // Customer info from first order
  const firstOrder = orders[0]
  const customerName = firstOrder?.customer_name || project.customer_group?.name
  const customerEmail = firstOrder?.customer_email
  const customerPhone = firstOrder?.customer_phone
  const displayStatus = projectDisplayStatus(project.status, orders)
  // Net stays unknown until freight, install and storage costs are known (loading or failed).
  // Shipment freight is already in each order's Net, so only the rest is added here.
  const additionalNet = apCosts
    ? Number(apCosts.additional_net_amount) -
      apCosts.freight.shipments.reduce((sum, shipment) => sum + Number(shipment.cost_amount), 0)
    : null

  return (
    <div className="mx-auto max-w-[1600px]">
      {/* Header */}
      <div className="mb-5">
        <BackButton fallback={{ to: '/projects' }} fallbackLabel="Back to Projects" />
        <div className="mt-1 min-w-0 flex-1 space-y-2">
          {/* Row 1: title + financials (financials drop under the title below desktop) */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
            <div className="min-w-0">
              {/* On a phone the status pill flows right after the title's last word. */}
              <div className="sm:flex sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
                <h1 className="inline text-lg font-bold leading-snug tracking-tight sm:block sm:text-xl md:text-2xl">
                  {project.name}
                </h1>
                <span className="ml-2 inline-block align-[3px] sm:ml-0 sm:align-baseline">
                  <ManualStatusControl
                    entityLabel="project"
                    currentStatus={project.status}
                    statusRevision={project.status_revision}
                    statusSource={project.status_source}
                    statuses={PROJECT_STATUS_CHOICES}
                    loading={correctStatus.isPending}
                    onSubmit={(body, callbacks) => correctStatus.mutate(body, callbacks)}
                    triggerLabel={
                      project.status_summary && project.status === 'active'
                        ? project.status_summary.headline.label
                        : formatStatus(displayStatus)
                    }
                    trigger={
                      project.status_summary && project.status === 'active' ? (
                        <StatusPill tone={project.status_summary.headline.tone} className="text-xs">
                          {project.status_summary.headline.label}
                        </StatusPill>
                      ) : (
                        <Badge variant={displayStatus === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {formatStatus(displayStatus)}
                        </Badge>
                      )
                    }
                  />
                </span>
                <span className="ml-1.5 inline-block align-[3px] sm:ml-0 sm:align-baseline">
                  <ProjectPriorityPicker projectUuid={project.uuid} priority={project.priority} />
                </span>
              </div>
              {project.description && (
                <p className="text-sm text-muted-foreground truncate mt-0.5">{project.description}</p>
              )}
              {project.status_summary && (
                <OrderStatusChips
                  className="mt-1.5"
                  order={project.status_summary.order}
                  shipping={project.status_summary.shipping}
                  ap={project.status_summary.ap}
                  ar={project.status_summary.ar}
                />
              )}
              <ProjectDeliveryDates
                className="mt-2"
                timelines={deliveryTimeline.data ?? []}
                deliverByEditor={
                  <span className="contents">
                    <ProjectServiceTierPicker projectUuid={project.uuid} tier={asServiceTier(project.service_tier)} />
                    {/* The dates read in order: started, deliver by, then when it all ships and arrives. */}
                    <StartedDate startedAt={project.created_at} />
                    <ProjectNeedByPicker projectUuid={project.uuid} needBy={project.need_by} />
                  </span>
                }
              />
              {/* Order and email work in flight; each PO shows its own under its statuses. */}
              <div className="mt-1.5 empty:hidden">
                <ProjectActivityBadges
                  orderIds={orders.map((o) => o.id)}
                  communicationThreadIds={(threads ?? []).map((t) => t.id)}
                  variant="text"
                />
              </div>
            </div>
            <ProjectFinancialMetrics orders={orders} additionalNet={additionalNet} />
          </div>
          {/* Row 2: team pills + progress stepper */}
          <TooltipProvider>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:items-center">
                <ProjectStaffAssignment
                  projectId={projectId}
                  label="Sales"
                  className="max-sm:w-full max-sm:max-w-none"
                  idField="sales_person_id"
                  objField="sales_person"
                  currentUser={project.sales_person}
                  attention={actionRoles.has('sales')}
                />
                <ProjectStaffAssignment
                  projectId={projectId}
                  label="Ops"
                  className="max-sm:w-full max-sm:max-w-none"
                  idField="ops_person_id"
                  objField="ops_person"
                  currentUser={project.ops_person}
                  attention={actionRoles.has('ops')}
                />
                <ProjectStaffAssignment
                  projectId={projectId}
                  label="Warehouse"
                  className="max-sm:w-full max-sm:max-w-none"
                  idField="warehousing_person_id"
                  objField="warehousing_person"
                  currentUser={project.warehousing_person}
                  attention={actionRoles.has('warehousing')}
                />
                <ProjectStaffAssignment
                  projectId={projectId}
                  label="Accounting"
                  className="max-sm:w-full max-sm:max-w-none"
                  idField="accounting_person_id"
                  objField="accounting_person"
                  currentUser={project.accounting_person}
                  attention={actionRoles.has('accounting')}
                />
              </div>
              <div className="shrink-0 self-start md:self-auto">
                <ProjectProgressStepper
                  orders={orders}
                  poReview={project.status_summary?.headline.key === 'po_review'}
                  billedThroughInvoices={billsThroughInvoices(customerPayments)}
                />
              </div>
            </div>
          </TooltipProvider>
        </div>
      </div>

      {/* Two columns on desktop: left = orders, actions, timelines; right = sticky sidebar. Below
          desktop both columns dissolve into one list in reading order: actions, orders, expected
          timeline, customer, payments, notes, documents, history, settings, financials. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* LEFT COLUMN — Main content */}
        <div className="min-w-0 max-lg:contents lg:space-y-6">

          {selectedPOs.length > 0 && (
            // Floats above the page so selecting never moves the POs under the pointer.
            <div
              role="group"
              aria-label="Selected purchase orders"
              className="fixed inset-x-0 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-30 mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center justify-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-lg sm:bottom-[calc(1.25rem+env(safe-area-inset-bottom))] md:translate-x-[100px]"
            >
              <span className="text-sm font-medium tabular-nums">
                {selectedPOs.length} {selectedPOs.length === 1 ? 'PO' : 'POs'} selected
              </span>
              {selectedReviewable.length > 0 && (
                <LoadingButton
                  size="sm"
                  className="h-8"
                  loading={poReview.marking}
                  loadingText="Saving..."
                  onClick={() =>
                    poReview.request(
                      selectedReviewable.map((po) => po.uuid),
                      true,
                      () => setSelectedPOIds(new Set()),
                    )
                  }
                >
                  <CheckCircle2 aria-hidden="true" />
                  Mark reviewed
                </LoadingButton>
              )}
              <BulkPOStatusPicker
                pos={selectedPOs}
                variant={selectedReviewable.length > 0 ? 'outline' : 'default'}
                onSaved={() => setSelectedPOIds(new Set())}
              />
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setSelectedPOIds(new Set())}>
                Clear
              </Button>
            </div>
          )}

          {/* Per-order sections with PO sub-groups */}
          {orders.map((order) => {
            const orderPOs = order.purchase_orders ?? []
            const lineItems = order.line_items ?? []
            const { byPurchaseOrderId, manualItems, unassignedItems } = groupProjectOrderLines(
              orderPOs,
              lineItems,
            )
            const orderRevenue = calculateProjectFinancials([order]).revenue
            const purchasedShipping = purchasedShippingService(
              order.shopify_order?.shipping_lines_json,
            )
            const posExpanded = expandedOrders.has(order.id)

            return (
              <Card key={order.id} className="gap-0 py-0 max-lg:order-2">
                <CardContent className="space-y-3 p-4">
                  {/* Order header */}
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.uuid }}
                        className="font-semibold text-primary hover:underline"
                      >
                        {order.name || order.order_number || `Order #${order.id}`}
                      </Link>
                      <StatusPill tone={paymentTone(order.financial_status)}>
                        {order.financial_status ? sentenceCase(order.financial_status) : 'Unpaid'}
                      </StatusPill>
                      <StatusPill tone={fulfillmentTone(order.fulfillment_status)}>
                        {order.fulfillment_status ? sentenceCase(order.fulfillment_status) : 'Unfulfilled'}
                      </StatusPill>
                    </div>
                    <span className="flex shrink-0 items-baseline gap-4 text-sm tabular-nums">
                      {(order.shipping_total != null || purchasedShipping) && (
                        <span title={purchasedShipping ? `Purchased shipping: ${purchasedShipping}` : undefined}>
                          <span className="mr-1 text-xs text-muted-foreground">Shipping</span>
                          {order.shipping_total != null ? formatMoney(order.shipping_total) : purchasedShipping}
                        </span>
                      )}
                      <span className="font-semibold">
                        <span className="mr-1 text-xs font-normal text-muted-foreground">Revenue</span>
                        {orderRevenue == null ? '—' : formatMoney(orderRevenue)}
                      </span>
                    </span>
                  </div>

                  {order.shopify_order?.note?.trim() && (
                    <section
                      aria-labelledby={`shopify-order-note-${order.id}`}
                      className="rounded-lg bg-muted/50 px-3 py-2.5"
                    >
                      <p
                        id={`shopify-order-note-${order.id}`}
                        className="text-xs font-medium text-muted-foreground"
                      >
                        Shopify order note
                      </p>
                      <p className="mt-1 whitespace-pre-wrap [overflow-wrap:anywhere] text-sm">
                        {order.shopify_order.note.trim()}
                      </p>
                    </section>
                  )}

                  {/* PO sub-groups within this order: the first three, the rest on request */}
                  <div id={`order-${order.id}-purchase-orders`} className="space-y-3">
                    {(posExpanded ? orderPOs : orderPOs.slice(0, FOLD_COUNT)).map((po) => (
                      <ProjectPOGroup
                        key={po.id}
                        po={po}
                        lines={byPurchaseOrderId.get(po.id) ?? []}
                        timeline={timelinesByPO.get(po.id)}
                        details={
                          po.po_review ? (
                            <HeldPurchaseOrderDetails
                              projectUuid={projectId}
                              purchaseOrderUuid={po.uuid}
                              marking={poReview.markingUuids.includes(po.uuid)}
                              onMark={(uuids, reviewed) => poReview.request(uuids, reviewed)}
                            />
                          ) : undefined
                        }
                        selected={selectedPOIds.has(po.id)}
                        onSelectedChange={(on) =>
                          setSelectedPOIds((current) => {
                            const next = new Set(current)
                            if (on) next.add(po.id)
                            else next.delete(po.id)
                            return next
                          })
                        }
                      />
                    ))}
                  </div>
                  {orderPOs.length > FOLD_COUNT && (
                    <FoldToggle
                      expanded={posExpanded}
                      total={orderPOs.length}
                      noun="purchase orders"
                      controls={`order-${order.id}-purchase-orders`}
                      onToggle={() =>
                        setExpandedOrders((current) => {
                          const next = new Set(current)
                          if (posExpanded) next.delete(order.id)
                          else next.add(order.id)
                          return next
                        })
                      }
                    />
                  )}
                  <ProjectManualGroup lines={manualItems} />
                  <ProjectUnassignedGroup lines={unassignedItems} />
                </CardContent>
              </Card>
            )
          })}

          {orders.length === 0 && (
            <div className="text-center text-muted-foreground py-12 max-lg:order-2">No orders yet</div>
          )}

          {/* Actions: below the orders on desktop, first below desktop */}
          <div className="min-w-0 max-lg:order-1">
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Actions</h2>
            <ProjectActionList
              actions={projectActions}
              projectId={projectId}
              team={project}
              focusUuid={actionUuid}
              listId="project-actions"
            />
          </div>

          <div className="min-w-0 max-lg:order-3">
            <ProjectExpectedTimeline
              timelines={deliveryTimeline.data ?? []}
              isLoading={deliveryTimeline.isLoading}
            />
          </div>

          <section aria-labelledby="project-timeline-title" className="min-w-0 space-y-2 max-lg:order-8">
            <h2
              id="project-timeline-title"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Timeline
            </h2>
            <TimelinePanel projectId={projectId} />
          </section>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div className="min-w-0 max-lg:contents lg:sticky lg:top-4 lg:self-start lg:space-y-5">
          <div className="min-w-0 max-lg:order-4">
            <ProjectCustomerCard
              projectId={projectId}
              name={customerName}
              customerGroupUuid={project.customer_group?.uuid}
              email={customerEmail}
              phone={customerPhone}
              shipTo={firstOrder}
              threads={customerThreads}
              threadsLoading={customerThreadsLoading}
            />
          </div>

          {/* Notes / Comments */}
          <Card className="gap-0 py-0 max-lg:order-6">
            <CardContent className="space-y-3 p-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Notes / Comments
              </h3>
              <CommentForm projectUuid={projectId} />
              <CommentList projectUuid={projectId} />
            </CardContent>
          </Card>

          <div className="min-w-0 max-lg:order-5">
            <ProjectMoneyCard
              projectId={projectId}
              apCosts={apCosts}
              apCostsLoading={apCostsLoading}
              apCostsError={apCostsError}
            />
          </div>

          <div className="min-w-0 max-lg:order-7">
            <ProjectDocumentsCard
              projectId={projectId}
              documents={documents}
              purchaseOrders={allPurchaseOrders}
            />
          </div>

          <div className="min-w-0 max-lg:order-9">
            <ProjectSettingsPanel
              projectId={projectId}
              projectName={project.name}
              projectStatus={project.status}
              trackingEnabled={project.tracking_enabled}
            />
          </div>

          {/* Financials (mobile only — shown in header on desktop) */}
          <Card className="gap-0 py-0 md:hidden max-lg:order-10">
            <CardContent className="space-y-2 p-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Financials</h3>
              <ProjectFinancialMetrics orders={orders} additionalNet={additionalNet} variant="mobile" />
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  )
}
